import type {
  ClassLifecycleSummary,
  GraduationCriteria,
  LifecycleEvent,
  PromotionCriteria,
  StudentGraduation,
  StudentPromotion,
  StudentTransfer,
  TransferType,
} from "../../services/lifecycleService";
import type { Student } from "../../services/studentService";
import { byNumber, byText, humanise, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * Learners moving through the school: promotions, transfers, graduations and
 * changes of standing. The gates here mirror the server's (approve_promotion,
 * approve_graduation, the transfer actions, change_student_lifecycle_status),
 * so the page only offers what the server will accept and says why not.
 */

export const day = (value?: string | null) => (value ? value.slice(0, 10) : "");
/** Decimal fields arrive as strings ("82.50"); "82.5%". */
export const pct = (value?: string | number | null) => (value === null || value === undefined || value === "" ? "—" : `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`);

type Tone = { label: string; dot: string };

export const PROMOTION_STATUS: Record<StudentPromotion["status"], Tone> = {
  pending: { label: "Waiting for a decision", dot: "bg-amber-500" },
  approved: { label: "Approved", dot: "bg-sky-500" },
  completed: { label: "Promoted", dot: "bg-emerald-500" },
  rejected: { label: "Rejected", dot: "bg-red-500" },
};

export const TRANSFER_STATUS: Record<StudentTransfer["status"], Tone> = {
  requested: { label: "Requested", dot: "bg-amber-500" },
  approved: { label: "Approved, to complete", dot: "bg-sky-500" },
  completed: { label: "Completed", dot: "bg-emerald-500" },
  rejected: { label: "Rejected", dot: "bg-red-500" },
  cancelled: { label: "Cancelled", dot: "bg-gray-400" },
};

export const GRADUATION_STATUS: Record<StudentGraduation["status"], Tone> = {
  eligible: { label: "Waiting for a decision", dot: "bg-amber-500" },
  approved: { label: "Approved", dot: "bg-sky-500" },
  graduated: { label: "Graduated", dot: "bg-emerald-500" },
  rejected: { label: "Rejected", dot: "bg-red-500" },
};

export const LEARNER_STATUS: Record<string, Tone> = {
  active: { label: "In school", dot: "bg-emerald-500" },
  suspended: { label: "Suspended", dot: "bg-amber-500" },
  withdrawn: { label: "Withdrawn", dot: "bg-gray-400" },
  expelled: { label: "Expelled", dot: "bg-red-500" },
  transferred: { label: "Transferred out", dot: "bg-sky-500" },
  graduated: { label: "Graduated", dot: "bg-indigo-500" },
  inactive: { label: "Inactive", dot: "bg-gray-400" },
};

export const DIRECTIONS: Record<StudentTransfer["direction"], string> = {
  internal: "Between classes",
  out: "To another school",
  in: "From another school",
};

export const EVENT_LABELS: Record<LifecycleEvent["event_type"], string> = {
  enrollment: "Enrolment / restored",
  promotion: "Promotion",
  transfer_in: "Transfer in / class move",
  transfer_out: "Transfer out",
  graduation: "Graduation",
  withdrawal: "Withdrawal",
  suspension: "Suspension",
  expulsion: "Expulsion",
};

const tone = <K extends string>(map: Record<K, Tone>, key: K) => map[key] ?? { label: humanise(key), dot: "bg-gray-400" };
export const promotionStatus = (status: StudentPromotion["status"]) => tone(PROMOTION_STATUS, status);
export const transferStatus = (status: StudentTransfer["status"]) => tone(TRANSFER_STATUS, status);
export const graduationStatus = (status: StudentGraduation["status"]) => tone(GRADUATION_STATUS, status);
export const learnerStatus = (status: string) => LEARNER_STATUS[status] ?? { label: humanise(status), dot: "bg-gray-400" };

/** One requirement against what the learner has: "Attendance 82% · needs 75%". */
export interface Check {
  label: string;
  value: string;
  need: string;
  ok: boolean;
}

export function promotionChecks(values: { attendance_percentage: string | number; average_grade: string | number; behavior_incidents: number }, criteria?: Pick<PromotionCriteria, "min_attendance_percentage" | "min_average_grade" | "max_behavior_incidents">): Check[] {
  if (!criteria) return [];
  return [
    { label: "Attendance", value: pct(values.attendance_percentage), need: `at least ${pct(criteria.min_attendance_percentage)}`, ok: Number(values.attendance_percentage) >= Number(criteria.min_attendance_percentage) },
    { label: "Average mark", value: pct(values.average_grade), need: `at least ${pct(criteria.min_average_grade)}`, ok: Number(values.average_grade) >= Number(criteria.min_average_grade) },
    { label: "Behaviour incidents", value: String(values.behavior_incidents), need: `at most ${criteria.max_behavior_incidents}`, ok: Number(values.behavior_incidents) <= Number(criteria.max_behavior_incidents) },
  ];
}

export function graduationChecks(record: Pick<StudentGraduation, "attendance_percentage" | "average_grade" | "community_service_hours" | "extracurricular_participation" | "final_exam_passed">, criteria?: GraduationCriteria): Check[] {
  if (!criteria) return [];
  const checks: Check[] = [
    { label: "Attendance", value: pct(record.attendance_percentage), need: `at least ${pct(criteria.min_attendance_percentage)}`, ok: Number(record.attendance_percentage) >= Number(criteria.min_attendance_percentage) },
    { label: "Average mark", value: pct(record.average_grade), need: `at least ${pct(criteria.min_average_grade)}`, ok: Number(record.average_grade) >= Number(criteria.min_average_grade) },
  ];
  if (criteria.community_service_hours > 0) checks.push({ label: "Community service", value: `${record.community_service_hours} h`, need: `at least ${criteria.community_service_hours} h`, ok: record.community_service_hours >= criteria.community_service_hours });
  if (criteria.extracurricular_participation) checks.push({ label: "Clubs or sport", value: record.extracurricular_participation ? "Yes" : "No", need: "required", ok: record.extracurricular_participation });
  if (criteria.final_exam_passed) checks.push({ label: "Final exam", value: record.final_exam_passed ? "Passed" : "Not passed", need: "must pass", ok: record.final_exam_passed });
  return checks;
}

/** Why a pending promotion can't be approved yet; empty when it can. */
export function promotionBlockers(row: StudentPromotion, criteria?: PromotionCriteria) {
  if (row.status !== "pending") return [];
  const blockers: string[] = [];
  if (criteria && !criteria.is_active) blockers.push("Its promotion criteria have been made inactive.");
  if (!row.meets_criteria) blockers.push("The learner does not meet the promotion criteria.");
  if (criteria?.requires_parent_approval && !row.parent_approved) blockers.push("A guardian must agree first, from the family portal.");
  return blockers;
}

export function graduationBlockers(row: StudentGraduation, criteria?: GraduationCriteria) {
  if (row.status !== "eligible") return [];
  const blockers: string[] = [];
  if (criteria && !criteria.is_active) blockers.push("Its graduation criteria have been made inactive.");
  if (!row.meets_criteria) blockers.push("The learner does not meet the graduation criteria.");
  return blockers;
}

/** The transfer's next steps, as the server allows them. */
export function transferActions(status: StudentTransfer["status"]) {
  return {
    approve: status === "requested",
    reject: status === "requested",
    complete: status === "approved",
    cancel: status === "requested" || status === "approved",
    remove: status === "requested",
  };
}

export type StandingChange = "active" | "suspended" | "withdrawn" | "expelled";

/** The standing a learner can be moved to from where they are. */
export function allowedStandingChanges(status: string): { value: StandingChange; label: string; help: string }[] {
  const suspend = { value: "suspended" as const, label: "Suspend", help: "Portal access is turned off; the learner keeps their class place." };
  const restore = { value: "active" as const, label: "Restore to active", help: "Portal access is turned back on." };
  const withdraw = { value: "withdrawn" as const, label: "Withdraw", help: "The learner leaves the school and their class place is released." };
  const expel = { value: "expelled" as const, label: "Expel", help: "The learner leaves the school and their class place is released." };
  if (status === "active") return [suspend, withdraw, expel];
  if (status === "suspended") return [restore, withdraw, expel];
  return [];
}

// ---- Registers ---------------------------------------------------------------

export const promotionConfig: RegisterConfig<StudentPromotion> = {
  search: (row) => `${row.student_name} ${row.student_id} ${row.from_class_name} ${row.to_class_name}`,
  views: [
    { key: "pending", label: "Waiting for a decision", match: (row) => row.status === "pending" },
    { key: "ready", label: "Meet the criteria", match: (row) => row.status === "pending" && row.meets_criteria },
    { key: "short", label: "Fall short", match: (row) => row.status === "pending" && !row.meets_criteria },
    { key: "completed", label: "Promoted", match: (row) => row.status === "completed" || row.status === "approved" },
    { key: "rejected", label: "Rejected", match: (row) => row.status === "rejected" },
    { key: "all", label: "All" },
  ],
  date: { label: "Proposed", value: (row) => row.created_at },
  category: { label: "From class", value: (row) => row.from_class_name },
  sorts: [
    { key: "newest", label: "Newest first", compare: reversed(byText((row) => row.created_at)) },
    { key: "name", label: "Learner A–Z", compare: byText((row) => row.student_name) },
    { key: "average", label: "Highest average", compare: reversed(byNumber((row) => row.average_grade)) },
    { key: "attendance", label: "Lowest attendance", compare: byNumber((row) => row.attendance_percentage) },
  ],
};

export const transferConfig: RegisterConfig<StudentTransfer> = {
  search: (row) => `${row.student_name} ${row.student_id} ${row.from_school} ${row.to_school} ${row.from_class_name ?? ""} ${row.to_class_name ?? ""} ${row.transfer_type_name}`,
  views: [
    { key: "open", label: "To act on", match: (row) => row.status === "requested" || row.status === "approved" },
    { key: "requested", label: "Requested", match: (row) => row.status === "requested" },
    { key: "approved", label: "Approved", match: (row) => row.status === "approved" },
    { key: "completed", label: "Completed", match: (row) => row.status === "completed" },
    { key: "closed", label: "Rejected or cancelled", match: (row) => row.status === "rejected" || row.status === "cancelled" },
    { key: "all", label: "All" },
  ],
  date: { label: "Effective", value: (row) => row.effective_date },
  category: { label: "Direction", value: (row) => DIRECTIONS[row.direction] },
  sorts: [
    { key: "newest", label: "Newest first", compare: reversed(byText((row) => row.created_at)) },
    { key: "effective", label: "Effective soonest", compare: byText((row) => row.effective_date) },
    { key: "name", label: "Learner A–Z", compare: byText((row) => row.student_name) },
  ],
};

export const graduationConfig: RegisterConfig<StudentGraduation> = {
  search: (row) => `${row.student_name} ${row.student_id} ${row.class_level_name} ${row.certificate_number} ${row.honors}`,
  views: [
    { key: "eligible", label: "Waiting for a decision", match: (row) => row.status === "eligible" },
    { key: "graduated", label: "Graduated", match: (row) => row.status === "graduated" || row.status === "approved" },
    { key: "rejected", label: "Rejected", match: (row) => row.status === "rejected" },
    { key: "all", label: "All" },
  ],
  date: { label: "Graduation", value: (row) => row.graduation_date },
  category: { label: "Class", value: (row) => row.class_level_name },
  sorts: [
    { key: "newest", label: "Newest first", compare: reversed(byText((row) => row.created_at)) },
    { key: "name", label: "Learner A–Z", compare: byText((row) => row.student_name) },
    { key: "average", label: "Highest average", compare: reversed(byNumber((row) => row.average_grade)) },
  ],
};

export const learnerConfig: RegisterConfig<Student> = {
  search: (row) => `${row.full_name} ${row.student_id} ${row.admission_number ?? ""} ${row.current_class_name ?? ""}`,
  views: [
    { key: "active", label: "In school", match: (row) => row.status === "active" },
    { key: "suspended", label: "Suspended", match: (row) => row.status === "suspended" },
    { key: "left", label: "Left the school", match: (row) => ["withdrawn", "expelled", "transferred", "graduated"].includes(row.status) },
    { key: "all", label: "All" },
  ],
  category: { label: "Class", value: (row) => row.current_class_name ?? "" },
  sorts: [
    { key: "name", label: "Name A–Z", compare: byText((row) => row.full_name) },
    { key: "class", label: "Class", compare: byText((row) => row.current_class_name) },
  ],
};

export const eventConfig: RegisterConfig<LifecycleEvent> = {
  search: (row) => `${row.student_name} ${row.student_id} ${row.description} ${row.notes} ${row.created_by_name ?? ""}`,
  views: [
    { key: "all", label: "All" },
    { key: "moves", label: "Promotions and moves", match: (row) => ["promotion", "transfer_in", "transfer_out"].includes(row.event_type) },
    { key: "graduation", label: "Graduations", match: (row) => row.event_type === "graduation" },
    { key: "standing", label: "Standing changes", match: (row) => ["suspension", "enrollment", "withdrawal", "expulsion"].includes(row.event_type) },
  ],
  date: { label: "Date", value: (row) => row.event_date },
  category: { label: "Event", value: (row) => EVENT_LABELS[row.event_type] },
  sorts: [
    { key: "newest", label: "Newest first", compare: reversed(byText((row) => `${row.event_date}${row.created_at}`)) },
    { key: "oldest", label: "Oldest first", compare: byText((row) => `${row.event_date}${row.created_at}`) },
    { key: "name", label: "Learner A–Z", compare: byText((row) => row.student_name) },
  ],
};

const openWork = (row: ClassLifecycleSummary) => row.pending_promotions + row.pending_graduations + row.transfer_requests;

export const classSummaryConfig: RegisterConfig<ClassLifecycleSummary> = {
  search: (row) => row.class_name,
  views: [
    { key: "all", label: "All" },
    { key: "open", label: "With work waiting", match: (row) => openWork(row) > 0 },
    { key: "unproposed", label: "No promotions proposed", match: (row) => row.total_students > 0 && row.pending_promotions === 0 },
  ],
  sorts: [
    { key: "school", label: "School order", compare: () => 0 },
    { key: "work", label: "Most work waiting", compare: reversed(byNumber(openWork)) },
    { key: "learners", label: "Most learners", compare: reversed(byNumber((row) => row.total_students)) },
  ],
};

const activeViews = <T extends { is_active: boolean }>() => [
  { key: "active", label: "Active", match: (row: T) => row.is_active },
  { key: "inactive", label: "Inactive", match: (row: T) => !row.is_active },
  { key: "all", label: "All" },
];

export const promotionCriteriaConfig: RegisterConfig<PromotionCriteria> = {
  search: (row) => `${row.name} ${row.from_class_name} ${row.to_class_name}`,
  views: activeViews<PromotionCriteria>(),
  category: { label: "From year", value: (row) => row.from_year_name },
  sorts: [
    { key: "from", label: "From class", compare: byText((row) => row.from_class_name) },
    { key: "name", label: "Name A–Z", compare: byText((row) => row.name) },
  ],
};

export const graduationCriteriaConfig: RegisterConfig<GraduationCriteria> = {
  search: (row) => `${row.name} ${row.class_level_name}`,
  views: activeViews<GraduationCriteria>(),
  category: { label: "Year", value: (row) => row.class_year_name },
  sorts: [{ key: "class", label: "Class", compare: byText((row) => row.class_level_name) }],
};

export const transferTypeConfig: RegisterConfig<TransferType> = {
  search: (row) => `${row.name} ${row.description}`,
  views: activeViews<TransferType>(),
  sorts: [{ key: "name", label: "Name A–Z", compare: byText((row) => row.name) }],
};

/** A class for a pick-list, with its year when classes of several years are listed. */
export const classOption = (row: { name: string; academic_year_name?: string }) => (row.academic_year_name ? `${row.name} (${row.academic_year_name})` : row.name);
